import _ from "lodash";

import { experiments as experimentsApi } from "apis-v2/callables";
import getLogger, { LogGroup } from "js/core/logger";
import getLocalStorage from "js/core/utilities/localStorage";
import { auth } from "js/firebase/auth";
import { trackActivity } from "js/core/utilities/utilities";
import { app } from "js/namespaces";
import { uuid } from "common/utils/uuid";

const logger = getLogger(LogGroup.EXPERIMENTS);

const ANALYTICS_REPORTING_CONFIG = {
    editor_ui_test: {
        event_category: "ExperimentGroup",
        event_action: "Assigned",
        experiment_id: "F55F277E15C8C47AD5F82E7C32615F66",
        experiment_group_assignment_label: {
            enabled: "test",
            disabled: "control"
        }
    },
    ai_gen_images: {
        event_category: "ExperimentGroup",
        event_action: "Assigned",
        experiment_id: "F8A3C6D9E2B7F4A1C5D8E6B3F9A2D7C4",
        experiment_group_assignment_label: {
            enabled: "test",
            disabled: "control"
        }
    },
    workspace_entry_generate_with_ai: {
        event_category: "ExperimentGroup",
        event_action: "Assigned",
        experiment_id: "B62F8C1E7A9D4C3F5E0B6A2D8C4F1A7E",
        experiment_group_assignment_label: {
            enabled: "test",
            disabled: "control"
        }
    }
};

const experimentsCache: Record<string, Record<string, { enabled: boolean, wasInitialized: boolean }>> = {};

const localStorage = getLocalStorage();

let browserId = localStorage.getItem("experiments-browser-id");
if (!browserId) {
    browserId = uuid();
    localStorage.setItem("experiments-browser-id", browserId);
}

function reportAnalytics(apiResponse: { [experimentId: string]: { enabled: boolean, wasInitialized: boolean } }) {
    Object.entries(apiResponse).forEach(([experimentId, { enabled, wasInitialized }]) => {
        if (!wasInitialized) {
            return;
        }

        const config = ANALYTICS_REPORTING_CONFIG[experimentId];
        if (!config) {
            return;
        }

        const label = enabled ? config.experiment_group_assignment_label.enabled : config.experiment_group_assignment_label.disabled;
        trackActivity(config.event_category, config.event_action, label, null, {
            experiment_id: config.experiment_id,
            experiment_group_assignment: label
        });
    });
}

function getOverrideStates(): { [experimentId: string]: boolean } {
    const overrideStates: { [experimentId: string]: boolean } = {};

    const overrideExperimentsJSON = localStorage.getItem("overrideExperiments");
    if (overrideExperimentsJSON) {
        const overrideExperiments: { [experimentId: string]: boolean } = JSON.parse(overrideExperimentsJSON);
        Object.entries(overrideExperiments).forEach(([experimentId, enabled]) => {
            overrideStates[experimentId] = enabled;
        });
    }

    return overrideStates;
}

function getMigrationStates(): { [experimentId: string]: boolean } {
    const migrationStates: { [experimentId: string]: boolean } = {};

    const signUpGroup = localStorage.getItem("signUpGroup");
    if (signUpGroup) {
        migrationStates["sign_up_b"] = signUpGroup === "B";
        localStorage.removeItem("signUpGroup");
    }

    return migrationStates;
}

let getExperimentsPromiseChain = Promise.resolve();
export async function getExperiments(experimentIds: string[]): Promise<Record<string, { enabled: boolean, wasInitialized: boolean }>> {
    return new Promise((resolve, reject) => {
        // Chain to ensure we don't execute similar requests in parallel
        getExperimentsPromiseChain = getExperimentsPromiseChain
            .then(async () => {
                try {
                    const cacheId = auth().currentUser?.uid ?? browserId;

                    if (!experimentsCache[cacheId]) {
                        experimentsCache[cacheId] = {};
                    }

                    const missingExperiments = experimentIds.filter(id => !(id in experimentsCache[cacheId]));
                    if (missingExperiments.length > 0) {
                        const overrideStates = getOverrideStates();
                        const migrationStates = getMigrationStates();

                        const { experiments } = await experimentsApi.getExperiments({ ids: missingExperiments, browserId, migrationStates, overrideStates });

                        reportAnalytics(experiments);

                        _.assign(experimentsCache[cacheId], experiments);
                    }

                    return _.pick(experimentsCache[cacheId], experimentIds);
                } catch (err) {
                    logger.error(err, "[getExperiments] failed");
                    throw err;
                }
            })
            .then(resolve)
            .catch(reject);
    });
}

/**
 * Initializes all experiments for the current user, moving any experiments that are already initialized for the browser to the user.
 */
export async function initializeAllExperiments() {
    const uid = auth().currentUser?.uid;
    if (!uid) {
        throw new Error("User not logged in");
    }

    const overrideStates = getOverrideStates();
    const migrationStates = getMigrationStates();

    const { experiments } = await experimentsApi.initializeAllExperiments({ browserId, migrationStates, overrideStates });

    reportAnalytics(experiments);

    return experiments;
}

export async function getExperimentAnalyticsProps(experimentId: string) {
    const config = ANALYTICS_REPORTING_CONFIG[experimentId];
    if (!config) {
        return {};
    }

    const experiments = await getExperiments([experimentId]);
    const experiment = experiments[experimentId];
    if (!experiment) {
        return {};
    }

    return {
        experiment_id: config.experiment_id,
        experiment_group_assignment: experiment.enabled ? config.experiment_group_assignment_label.enabled : config.experiment_group_assignment_label.disabled,
        experiment_variant: experiment.enabled ? "variant" : "control",
        device_type: app.isMobileOrTablet ? "mobile" : "desktop",
        ...(config.version && { experiment_version: config.version }),
        ...(config.name && { experiment_name: config.name }),
    };
}

export async function trackExperimentEvent({
    experimentId,
    category,
    eventAction,
    extraProps = {},
}: {
    experimentId: string;
    category: string;
    eventAction: string;
    extraProps?: Record<string, unknown>;
}) {
    const config = ANALYTICS_REPORTING_CONFIG[experimentId];
    if (!config) {
        return;
    }

    const analyticsProps = await getExperimentAnalyticsProps(experimentId);
    trackActivity(
        category,
        eventAction,
        null,
        null,
        { workspace_id: "all", ...analyticsProps, ...extraProps },
        { audit: true }
    );
}
